import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AutoComplete } from "primereact/autocomplete";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import _ from "lodash";
import Map from "../Map/Map";
import API from "../../Api/Api";
import { API_END_POINT } from "../../Api/EndPoints";
import { fetchAppCustomersMap } from "../../REDUX/actions/appCustomers";
import { fetchCompetitorsMap } from "../../REDUX/actions/competitors";
import { openRightModal } from "../../REDUX/actions/rightModal";
import AppCustomerModal from "./AppCustomerModal";
import CompetitorModal from "./Competitors/CompetitorModal";
import StatisticsSection from "./Statistics";

/**
 * "Carte" tab — same shell as the prospect map (search, colored toggle
 * buttons, refresh): every scoped app customer that has a marker. Clients are
 * green, prospects brown/orange — reuses Marker's areaStatus color classes
 * (AVAILABLE = green, TO_SEIZE = orange).
 */
const AppCustomerMap = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { mapCustomers, mapLoading } = useSelector(
    (state) => state.appCustomersReducer
  );
  const { mapCompetitors } = useSelector((state) => state.competitorsReducer);
  const { user } = useSelector((state) => state.userReducer);
  const { scope } = useSelector((state) => state.appCustomersReducer);

  const [value, setValue] = useState("");
  const [items, setItems] = useState([]);
  const [geoEntities, setGeoEntities] = useState([]);
  // All shown by default; toggling hides that role's markers.
  const [visibleRoles, setVisibleRoles] = useState([5, 6]);
  const [showCompetitors, setShowCompetitors] = useState(true);

  // Reseller stores (house icon + catchment polygons), via
  // the SAME custom geo-entity query the partners map uses (no defaultQuery):
  // the controller computes the reseller's soldSpa (Sales Year popup row) and
  // the populated note/contract feed the partners-map popup fields.
  const fetchGeoEntities = async (signal) => {
    try {
      const userRole = user?.attributes?.userRole;
      const ownResellerId =
        user?.attributes?.profil?.data?.attributes?.reseller?.data?.id;
      const affiliateId = user?.attributes?.business_affiliate?.data?.id;
      const marqueId = user?.attributes?.marque?.data?.id;
      // Stores shown: the reseller's own; else the picked store; else the
      // affiliate's stores (its own, or the admin's picked affiliate); else all.
      const resellerFilter =
        userRole === 0
          ? { id: { $eq: ownResellerId ?? -1 } }
          : scope.organizationId
            ? { id: { $eq: scope.organizationId } }
            : userRole === 2 || scope.businessAffiliateId
              ? {
                  business_affiliate: {
                    id: {
                      $eq:
                        userRole === 2
                          ? (affiliateId ?? -1)
                          : scope.businessAffiliateId,
                    },
                  },
                }
              : { id: { $notNull: true } };
      const { data } = await API.get(`${API_END_POINT}api/geo-entities`, {
        params: {
          // Every status → no marker filtered out by the controller.
          statusList: ["AVAILABLE", "TO_SEIZE", "TAKEN"],
          reseller: true,
          populate: {
            reseller: { populate: ["note", "contract"] },
          },
          filters: {
            reseller: {
              ...resellerFilter,
              ...(userRole !== 0 && marqueId
                ? { marque: { id: { $eq: marqueId } } }
                : {}),
            },
          },
        },
        signal,
      });
      setGeoEntities(data?.data ?? []);
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
    }
  };

  const abortRef = useRef(null);
  const fetch = () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    dispatch(fetchAppCustomersMap(abortRef.current.signal));
    dispatch(fetchCompetitorsMap(abortRef.current.signal));
    fetchGeoEntities(abortRef.current.signal);
  };

  useEffect(() => {
    fetch();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const customerName = (customer) =>
    [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  const customerAddress = (customer) =>
    [
      customer.address?.street,
      customer.address?.zip,
      customer.address?.city?.name,
    ]
      .filter(Boolean)
      .join(" ");
  // Case- and accent-insensitive ("Hélène" matches "helene").
  const normalize = (text) =>
    String(text ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  // Markers matching the query: client / prospect name, client order number,
  // customer postal address, competitor company name. Items share the mapbox
  // shape (center, place_name, context[0].text = the subtitle).
  const localMatches = (query) => {
    const q = normalize(query);
    if (!q) return [];
    const matches = [];
    for (const customer of mapCustomers) {
      const center = customer.markers?.[0]?.coordinates;
      if (!center) continue;
      const name = customerName(customer);
      const kind = t(customer.userRole === 6 ? "Client" : "Prospect");
      const orderNumber = (customer.orderNumbers ?? []).find((number) =>
        normalize(number).includes(q)
      );
      const address = customerAddress(customer);
      let subtitle = null;
      if (normalize(name).includes(q)) subtitle = kind;
      else if (orderNumber) subtitle = `${kind} — ${t("Order")} ${orderNumber}`;
      else if (normalize(address).includes(q)) subtitle = `${kind} — ${address}`;
      if (subtitle) {
        matches.push({ center, place_name: name, context: [{ text: subtitle }] });
      }
    }
    for (const competitor of mapCompetitors) {
      const center = competitor?.attributes?.markers?.[0]?.coordinates;
      const name = competitor?.attributes?.companyName;
      if (center && normalize(name).includes(q)) {
        matches.push({
          center,
          place_name: name,
          context: [{ text: t("Competitor") }],
        });
      }
    }
    return matches.slice(0, 10);
  };

  // Suggestions: the matching markers first, then mapbox places (any postal
  // address, flown to on select).
  const search = _.debounce(async (event) => {
    const { query } = event;
    const local = localMatches(query);
    try {
      const { data } = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`
      );
      setItems([...local, ...(data?.features || [])]);
    } catch (error) {
      setItems(local);
    }
  }, 300);

  const toggleRole = (role) =>
    setVisibleRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );

  const features = [
    ...mapCustomers
      .filter((customer) => visibleRoles.includes(customer.userRole))
      .map((customer) => ({
        // Prefixed: customer ids and geo-entity ids live in the same key space.
        id: `customer-${customer.id}`,
        markers: customer.markers ?? [],
        name: customerName(customer),
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phoneNumber: customer.phoneNumber,
        // Dedicated popup field list + "Voir plus" opening the edit modal.
        isAppCustomer: true,
        onSeeMore: () =>
          dispatch(openRightModal(<AppCustomerModal customer={customer} />)),
        // Marker color hook: AVAILABLE = green (client), TO_SEIZE =
        // brown/orange (prospect).
        isProspect: true,
        areaStatus: (customer.markers ?? []).map(() =>
          customer.userRole === 6 ? "AVAILABLE" : "TO_SEIZE"
        ),
      })),
    // Resellers: house marker + turf polygons, with the partners-map popup
    // fields (Objectif Spa/Contract, Sales Year = soldSpa, Note, Website,
    // Email, Phone) spread from the populated reseller attributes.
    ...geoEntities
      .filter((entity) => entity?.attributes?.reseller?.data)
      .map((entity) => ({
        ...(entity.attributes.reseller.data.attributes ?? {}),
        id: `org-${entity.id}`,
        markers: entity?.attributes?.markers ?? [],
        turfs: entity?.attributes?.turfs,
        name: entity?.attributes?.reseller?.data?.attributes?.name,
      })),
    // Competitors (red store icon): the competitor collection only.
    ...(showCompetitors
      ? mapCompetitors.map((competitor) => ({
            id: `competitor-${competitor.id}`,
            markers: competitor?.attributes?.markers ?? [],
            name: competitor?.attributes?.companyName,
            phoneNumber: competitor?.attributes?.phoneNumber,
            email: competitor?.attributes?.email,
            isCompetitor: true,
            onSeeMore: () =>
              dispatch(
                openRightModal(<CompetitorModal competitor={competitor} />)
              ),
          }))
      : []),
  ];

  return (
    <div className="position-relative z-0">
      {mapLoading && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 z-9999 d-flex align-items-center justify-content-center">
          <Spinner />
        </div>
      )}
      <h2 className="text-blue bold f22 quickSand mt-2 mb-3">
        {t("Catchment Area Map")}
      </h2>
      <div className="d-flex mb-3 flex-wrap gap-3">
        <div className="">
          <AutoComplete
            field="place_name"
            placeholder={t("Name, company, order number or address")}
            className="customAutocomplete br6 w-100 z-1"
            value={value}
            suggestions={items}
            completeMethod={search}
            onChange={(e) => setValue(e.value)}
            itemTemplate={(item) => (
              <div className="d-flex flex-column">
                <span className="f16 bold">{item?.place_name}</span>
                {item?.context?.length > 0 && (
                  <span className="f14">{item?.context[0]?.text}</span>
                )}
              </div>
            )}
            style={{ height: "40px", border: "none", outline: "none" }}
          />
        </div>
        <div className="w-100 max-w-200px">
          <button
            className={`w-100 h-40px br5 ${
              visibleRoles.includes(6)
                ? "bg-green-600 border-0 text-white"
                : "border-green-600 border-2 text-green-600 bg-white"
            }`}
            onClick={() => toggleRole(6)}
          >
            {t("Client")}
          </button>
        </div>
        <div className="w-100 max-w-200px">
          <button
            className={`w-100 h-40px br5 ${
              visibleRoles.includes(5)
                ? "bg-orange-500 border-0 text-white"
                : "border-orange-500 border-2 text-orange-500 bg-white"
            }`}
            onClick={() => toggleRole(5)}
          >
            {t("Prospect")}
          </button>
        </div>
        <div className="w-100 max-w-200px">
          <button
            className={`w-100 h-40px br5 ${
              showCompetitors
                ? "bg-red-600 border-0 text-white"
                : "border-red-600 border-2 text-red-600 bg-white"
            }`}
            onClick={() => setShowCompetitors((prev) => !prev)}
          >
            {t("Competitor")}
          </button>
        </div>
      </div>
      <div className="position-relative">
        <button
          className="position-absolute top-0 start-0 p-3 border-0 h-60px w-60px d-flex align-items-center justify-content-center z-9999 f20 bg-transparent"
          onClick={fetch}
        >
          <i className="bi bi-arrow-clockwise f30 text-blue-700"></i>
        </button>
        <Map features={features} currentCenter={value?.center} />
      </div>
      {/* Statistics (spec "Onglet Statistique") — admin, reseller, affiliate. */}
      <StatisticsSection />
    </div>
  );
};

export default AppCustomerMap;
